import React, { forwardRef } from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'

interface ButtonProps {
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'accent' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  href?: string
  className?: string
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  external?: boolean
  icon?: React.ReactNode
  loading?: boolean
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-expanded'?: boolean
  'aria-controls'?: string
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      href,
      className,
      onClick,
      disabled = false,
      type = 'button',
      external = false,
      icon,
      loading = false,
      ...ariaProps
    },
    ref
  ) => {
    const baseClasses = cn(
      'btn inline-flex items-center justify-center font-medium rounded-md transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      {
        'btn-primary bg-primary text-white hover:bg-primary-dark': variant === 'primary',
        'btn-secondary bg-secondary text-white hover:bg-secondary-dark': variant === 'secondary',
        'btn-accent bg-accent text-white hover:bg-accent-dark': variant === 'accent',
        'btn-outline border border-gray-300 dark:border-gray-600 bg-transparent text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700':
          variant === 'outline',
        'py-1 px-3 text-sm': size === 'sm',
        'py-2 px-4 text-base': size === 'md',
        'py-3 px-6 text-lg': size === 'lg',
      },
      className
    )

    const isDisabled = disabled || loading

    // Loading spinner component
    const LoadingSpinner = () => (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    )

    // If href is provided, render as Link
    if (href && !isDisabled) {
      if (external) {
        return (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={href}
            className={baseClasses}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            {...ariaProps}
          >
            {loading && <LoadingSpinner />}
            {!loading && icon && (
              <span className="mr-2" aria-hidden="true">
                {icon}
              </span>
            )}
            {children}
            {external && !loading && (
              <svg
                className="ml-1 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            )}
          </a>
        )
      }

      return (
        <Link
          href={href}
          className={baseClasses}
          onClick={onClick}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...ariaProps}
        >
          {loading && <LoadingSpinner />}
          {!loading && icon && (
            <span className="mr-2" aria-hidden="true">
              {icon}
            </span>
          )}
          {children}
        </Link>
      )
    }

    // Otherwise render as button
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={baseClasses}
        onClick={onClick}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        {...ariaProps}
      >
        {loading && <LoadingSpinner />}
        {!loading && icon && (
          <span className="mr-2" aria-hidden="true">
            {icon}
          </span>
        )}
        <span>{children}</span>
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
